/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { getCatalogId, ICoreContext, msdyn365Commerce } from '@msdyn365-commerce/core-internal';
// import * as Msdyn365 from '@msdyn365-commerce/core';
import {
    CommerceProperty,
    ProductCatalog,
    ProductDeliveryOptions,
    ProductDimensionValue,
    ProductDimensionValueInventoryAvailability,
    ProductPrice,
    ReleasedProductType,
    SimpleProduct,
    InventoryAvailabilitySearchCriteria,
    ProductAvailableQuantity
} from '@msdyn365-commerce/retail-proxy';
import { BulkPurchase } from '@msdyn365-commerce-modules/bulk-purchase';
import {
    ArrayExtensions,
    DeliveryMode,
    FinitePromiseQueue,
    FinitePromiseQueueError,
    getDeliveryOptionsForSelectedVariant,
    GetDeliveryOptionsForSelectedVariantInput,
    getDimensionsForSelectedVariant,
    GetDimensionsForSelectedVariantInput,
    getInventoryLevelCodeFromDimensionValue,
    getPriceForSelectedVariant,
    getProductAvailabilitiesForSelectedVariant,
    getSelectedVariant,
    IDimensionsApp,
    IDimensionValueForSelectedVariant,
    InventoryLevelValues,
    IProductInventoryInformation,
    IPromiseQueue,
    PriceForSelectedVariantInput,
    ProductAvailabilitiesForSelectedVariantInput,
    SelectedVariantInput,
    setDimensionValuesToQuery
} from '@msdyn365-commerce-modules/retail-actions';
import { Button, getTelemetryObject, IModuleProps, INodeProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';
import { getEstimatedAvailabilityAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { getCustomerWarehouses_V2Async, getStockHoldStatusAsync } from '../../actions/DataActionExtension.g';

import {
    getBuyboxAddToCart,
    getBuyBoxInventoryLabel,
    getBuyboxKeyInPrice,
    getBuyboxProductAddToOrderTemplate,
    getBuyboxProductAddToWishlist,
    getBuyboxProductComparisonButton,
    getBuyboxProductDescription,
    getBuyboxProductPrice,
    getBuyboxProductQuantity,
    getBuyboxProductRating,
    getBuyboxProductTitle,
    getBuyboxProductUnitOfMeasure,
    getBuyboxShopSimilarButton,
    getQuantityLimitsMessages
} from './utilities/mil-buybox-components';
import { getBuyboxProductConfigure } from '@msdyn365-commerce-modules/buybox';
import {
    IBuyboxAddToCartViewProps,
    IBuyboxAddToOrderTemplateViewProps,
    IBuyboxAddToWishlistViewProps,
    IBuyboxCallbacks,
    IBuyboxCommonData,
    IBuyboxExtentedProps,
    IBuyboxKeyInPriceViewProps,
    IBuyboxProductConfigureViewProps,
    IBuyboxProductQuantityViewProps,
    IBuyboxShopSimilarLookViewProps,
    IErrorState,
    ShopSimiliarButtonType
} from '@msdyn365-commerce-modules/buybox';
import { IMilBuyboxState } from './utilities/mil-buyboxInterface';
import { IMilBuyboxData } from './mil-buybox.data';
import { IMilBuyboxProps, IMilBuyboxResources } from './mil-buybox.props.autogenerated';
import { getBuyboxFindInStore, IBuyboxFindInStoreViewProps } from './components/mil-buybox-find-in-store';
import { RecentlyViewedState } from '@msdyn365-commerce-modules/buybox';
import { displayedInventoryMethod } from '../mil-quick-order/mil-quick-order.props.autogenerated';
//import { IGetStockHoldStatusResponse } from '../../actions/DataServiceEntities.g';
//import { IGetStockHoldStatusResponse } from '../../actions/DataServiceEntities.g';

export interface CustomerWarehouseInventory {
    warehouseCode: string;
    warehouseName: string;
    inventory: number;
    transportDays: number;
    CustomerWarehouse: string;
}

export interface IMilBuyboxViewProps extends IMilBuyboxProps<IMilBuyboxData> {
    state: IMilBuyboxState;
    ModuleProps: IModuleProps;
    ProductInfoContainerProps: INodeProps;
    MediaGalleryContainerProps: INodeProps;
    callbacks: IBuyboxCallbacks;
    mediaGallery?: React.ReactNode;
    title?: React.ReactNode;
    description?: React.ReactNode;
    rating?: React.ReactNode;
    price?: React.ReactNode;
    productComparisonButton?: React.ReactNode;
    bulkPurchaseLink?: React.ReactNode;
    addToOrderTemplate?: IBuyboxAddToOrderTemplateViewProps;
    addToWishlist?: IBuyboxAddToWishlistViewProps;
    max: number | undefined;
    defaultMaxQty: number;
    addToCart: IBuyboxAddToCartViewProps;
    findInStore?: IBuyboxFindInStoreViewProps;
    quantity?: IBuyboxProductQuantityViewProps;
    configure?: IBuyboxProductConfigureViewProps;
    inventoryLabel?: React.ReactNode;
    shopSimilarLook?: IBuyboxShopSimilarLookViewProps;
    shopSimilarDescription?: IBuyboxShopSimilarLookViewProps;
    quantityLimitsMessages: React.ReactNode;
    telemetryContent?: ITelemetryContent;
    keyInPrice?: IBuyboxKeyInPriceViewProps;
    unitOfMeasure?: React.ReactNode;
    catalogs?: ProductCatalog[] | undefined;
    guaranteedItem: React.ReactNode;
}

/**
 * Buybox Module.
 */
class Buybox extends React.PureComponent<IMilBuyboxProps<IMilBuyboxData>, IMilBuyboxState> {
    /**
     * A queue of tasks of processing the changes in the dimensions.
     * Limit to two processes:
     * 1 - for the current process, which is under execution at the moment.
     * 2 - next process, which will process the latest version of data.
     * @remark Enqueueing new promises will discard the previous ones (except the one which is under processing).
     */
    private readonly dimensionUpdateQueue: IPromiseQueue<void> = new FinitePromiseQueue<void>(2);

    private readonly defaultMaxQty: number = 10000000;

    private dimensions: { [id: number]: string } = {};

    private readonly buyboxCallbacks: IBuyboxCallbacks = {
        updateQuantity: (newQuantity: number): boolean => {
            const errorState = { ...this.state.errorState };
            errorState.quantityError = undefined;
            errorState.otherError = undefined;

            this.setState({ quantity: newQuantity, errorState });
            return true;
        },
        updateErrorState: (newErrorState: IErrorState): void => {
            this.setState({ errorState: newErrorState });
        },
        updateSelectedProduct: (
            newSelectedProduct: Promise<SimpleProduct | null>,
            newInventory: IProductInventoryInformation | undefined,
            newPrice: ProductPrice | undefined,
            newDeliveryOptions: ProductDeliveryOptions | undefined
        ): void => {
            this.setState({
                selectedProduct: newSelectedProduct,
                productAvailableQuantity: newInventory,
                productDeliveryOptions: newDeliveryOptions
            });
            this._updatePrice(newPrice);
        },
        dimensionSelectedAsync: async (selectedDimensionId: number, selectedDimensionValueId: string): Promise<void> => {
            this.dimensions[selectedDimensionId] = selectedDimensionValueId;
            return this.dimensionUpdateQueue
                .enqueue(async () => {
                    return this._updateDimensions();
                })
                .catch((error: unknown) => {
                    // Ignore discarded processes.
                    if (error !== FinitePromiseQueueError.ProcessWasDiscardedFromTheQueue) {
                        throw error;
                    }
                });
        },
        getDropdownName: (dimensionType: number, resources: IMilBuyboxResources): string => {
            return this._getDropdownName(dimensionType, resources);
        },
        changeModalOpen: (isModalOpen: boolean): void => {
            this.setState({ modalOpen: isModalOpen });
        },
        changeUpdatingDimension: (isUpdatingDimension: boolean): void => {
            this.setState({ isUpdatingDimension });
        },

        /**
         * Update isUpdatingDeliveryOptions state.
         *
         * @param isUpdatingDeliveryOptions - The status of updating delivery options.
         */
        changeUpdatingDeliveryOptions: (isUpdatingDeliveryOptions: boolean): void => {
            this.setState({ isUpdatingDeliveryOptions });
        },

        updateKeyInPrice: (customPrice: number): void => {
            // Remove custom amount error when updating the custom price
            const errorState = { ...this.state.errorState };
            errorState.customAmountError = undefined;

            this.setState({ isPriceKeyedIn: true, keyInPriceAmount: customPrice, errorState });
            this._updatePrice(this.state.productPrice, customPrice);
        }
    };

    private readonly telemetryContent: ITelemetryContent;

    public constructor(props: IMilBuyboxProps<IMilBuyboxData>, state: IMilBuyboxState) {
        super(props);
        this.state = {
            errorState: {
                configureErrors: {}
            },
            quantity: undefined,
            min: undefined,
            max: undefined,
            selectedProduct: undefined,
            productPrice: undefined,
            productDeliveryOptions: undefined,

            modalOpen: false,
            isUpdatingDimension: false,
            isUpdatingDeliveryOptions: false,
            totalInventoryAvail: undefined,
            stockHoldStatusData: undefined,
            custWhInvAvail: undefined,
            cutoffTime: undefined,
            receiptCalendar: undefined
        };
        this.telemetryContent = getTelemetryObject(props.context.request.telemetryPageName!, props.friendlyName, props.telemetry);
    }

    public async componentDidMount(): Promise<void> {
        const {
            data: {
                product: { result: product },
                productPrice
            },
            context
        } = this.props;

        void productPrice.then(async result => {
            this._updatePrice(result);
        });

        if (product) {
            // Check if the product is service or not by product type
            if (product.ItemTypeValue === ReleasedProductType.Service) {
                this.setState({ isServiceItem: true });
            }
            await this._updateQuantitiesInState(product);
            const multipleOf = product.Behavior?.MultipleOfQuantity || 1;
            const minQty = product.Behavior?.MinimumQuantity || 1;
            let qty = 1;
            if (minQty && minQty >= multipleOf) {
                qty = minQty;
            } else if (multipleOf) {
                qty = multipleOf;
            }
            this.setState({ quantity: qty });
        }

        if (product && context.app.config.maxRecentlyViewedItemsCount > 0) {
            RecentlyViewedState.instance(this.props.context, context.app.config.maxRecentlyViewedItemsCount, product.RecordId);
        }
        await this.getWarehouseInventory();
    }

    public async componentDidUpdate(
        prevProps: Readonly<IMilBuyboxProps<IMilBuyboxData>>,
        prevState: Readonly<IMilBuyboxState>,
        snapshot?: any
    ): Promise<void> {
        if (prevState.quantity !== this.state.quantity) {
            await this.getWarehouseInventory();
        }
    }

    public async getWarehouseInventory(): Promise<void> {
        const {
            context,
            data,
            context: {
                app: {
                    config: { displayedInventoryMethod: inventoryMethod }
                }
            }
        } = this.props;

        const productRecId = data.product.result?.RecordId;
        if (!productRecId) {
            return;
        }
        const inventSearch: InventoryAvailabilitySearchCriteria = {
            ProductIds: [productRecId],
            DefaultWarehouseOnly: false,
            FilterByChannelFulfillmentGroup: true
        };
        const availabilities = getEstimatedAvailabilityAsync({ callerContext: context.actionContext }, inventSearch);
        const custAcctNum = context.request.user.customerAccountNumber || '';
        const customerWarehouses = getCustomerWarehouses_V2Async({ callerContext: context.actionContext }, custAcctNum);
        const itemId = data.product.result?.ItemId || '';
        const stockHoldStatuses = getStockHoldStatusAsync({ callerContext: context.actionContext }, custAcctNum, itemId);
        await Promise.all([availabilities, customerWarehouses, stockHoldStatuses]);

        const custWhRes = (await customerWarehouses).AvailableWarehouses;
        const availInvRes = (await availabilities).ProductWarehouseInventoryAvailabilities;

        const newCustInv: CustomerWarehouseInventory[] = [];
        if (custWhRes && availInvRes) {
            custWhRes.map(wh => {
                const inventoryInfo = availInvRes.find(inv => inv.InventLocationId === wh.AvailableWarehouse);
                if (inventoryInfo) {
                    const newWhEntry: CustomerWarehouseInventory = {
                        warehouseCode: wh.AvailableWarehouse || '',
                        warehouseName: wh.WarehouseName || '',
                        inventory:
                            inventoryMethod === displayedInventoryMethod.physicalAvailable
                                ? inventoryInfo.PhysicalAvailable || 0
                                : inventoryInfo.TotalAvailable || 0,
                        transportDays: wh.TransportDays || 0,
                        CustomerWarehouse: wh.CustomerWarehouse || ''
                    };
                    newCustInv.push(newWhEntry);
                }
            });
        }

        const aggProductInvAvail = availabilities.result?.AggregatedProductInventoryAvailabilities || [];
        const totalInv =
            custWhRes && custWhRes.length > 0 && newCustInv
                ? newCustInv?.reduce((acc, curr) => acc + (curr.inventory >= 0 ? curr.inventory : 0), 0)
                : aggProductInvAvail.length > 0 && inventoryMethod === displayedInventoryMethod.physicalAvailable
                ? aggProductInvAvail[0].PhysicalAvailableQuantity
                : aggProductInvAvail[0].TotalAvailableQuantity;

        const totalInventory = totalInv || 0;
        const baseInventoryRequired = this.state.quantity ?? this.state.min ?? 1;
        const availableQuantity = totalInventory >= baseInventoryRequired ? totalInventory : 0;
        this.setState({ totalInventoryAvail: totalInventory });
        const productAvailQuant: ProductAvailableQuantity = {
            ProductId: productRecId,
            AvailableQuantity: availableQuantity,
            UnitOfMeasure: availInvRes && availInvRes.length > 0 ? availInvRes[0].UnitOfMeasure : undefined
        };
        const productInventoryInfo: IProductInventoryInformation = {
            ProductAvailableQuantity: productAvailQuant,
            StockLevelLabel:
                totalInventory >= baseInventoryRequired
                    ? this.props.resources.availableStockLabelText
                    : this.props.resources.outOfStockText,
            IsProductAvailable: totalInventory >= baseInventoryRequired,
            InventLocationId: availInvRes && availInvRes.length > 0 ? availInvRes[0].InventLocationId : undefined, // double check
            deliveryType: DeliveryMode.shipping // double check
        };
        this.setState({ productAvailableQuantity: productInventoryInfo });
        this.setState({ stockHoldStatusData: await stockHoldStatuses });
        this.setState({
            custWhInvAvail: newCustInv,
            cutoffTime: (await customerWarehouses).DefaultCutoffTime,
            receiptCalendar: (await customerWarehouses).ReceiptCalendar
        });
    }

    public render(): JSX.Element | null {
        const {
            slots: { mediaGallery, productComparisonButton },
            data: {
                product: { result: product }
            },
            config: { className = '' }
        } = this.props;

        const { max, quantity } = this.state;

        if (!product) {
            this.props.context.telemetry.error('Product content is empty, module wont render');
            return null;
        }

        if (!quantity) {
            return null;
        }

        // Required to eliminate possible quantity being undefined TS errors
        const updatedState = { ...this.state, quantity: quantity };

        const props = this.props as IBuyboxExtentedProps<IBuyboxCommonData>;
        const defaultMinimumKeyInPrice = 10;
        const defaultMaximumKeyInPrice = 100;

        const context = this.props.context as ICoreContext<IDimensionsApp>;
        const inventoryLevel = context.app.config.inventoryLevel;
        const dimensionValuesWithInventory = ArrayExtensions.flatten(
            ArrayExtensions.validValues(this.props.data.productDimensions.result?.map(value => value.dimensionValuesWithInventory))
        );
        const hasAvailableProducts =
            !ArrayExtensions.hasElements(dimensionValuesWithInventory) ||
            dimensionValuesWithInventory.some((value: unknown) => {
                const inventoryLevelCode = getInventoryLevelCodeFromDimensionValue(
                    value as ProductDimensionValueInventoryAvailability,
                    inventoryLevel
                );
                return inventoryLevelCode !== InventoryLevelValues.outOfStock;
            });
        const catalogId = getCatalogId(this.props.context.request);
        const viewProps: IMilBuyboxViewProps = {
            ...(this.props as IMilBuyboxProps<IMilBuyboxData>),
            state: this.state, // new props in state
            mediaGallery: mediaGallery && mediaGallery.length > 0 ? mediaGallery[0] : undefined,
            ModuleProps: {
                moduleProps: this.props,
                className: classnames('ms-buybox', className)
            },
            ProductInfoContainerProps: {
                className: 'ms-buybox__content'
            },
            MediaGalleryContainerProps: {
                className: 'ms-buybox__media-gallery'
            },
            telemetryContent: this.telemetryContent,
            callbacks: this.buyboxCallbacks,
            title: getBuyboxProductTitle(props),
            description: getBuyboxProductDescription(props),
            bulkPurchaseLink: this._renderBuyboxBulkPurchaseLink(),
            unitOfMeasure: getBuyboxProductUnitOfMeasure(props),
            configure: getBuyboxProductConfigure(props, updatedState, this.buyboxCallbacks),
            findInStore: getBuyboxFindInStore(this.props, updatedState, this.buyboxCallbacks),
            price: getBuyboxProductPrice(props, updatedState),
            addToCart: getBuyboxAddToCart(
                props,
                updatedState,
                this.buyboxCallbacks,
                defaultMinimumKeyInPrice,
                defaultMaximumKeyInPrice,
                undefined,
                hasAvailableProducts
            ),
            productComparisonButton: ArrayExtensions.hasElements(productComparisonButton)
                ? getBuyboxProductComparisonButton(
                      productComparisonButton[0],
                      product,
                      getCatalogId(this.props.context.request),
                      updatedState
                  )
                : undefined,
            addToOrderTemplate: getBuyboxProductAddToOrderTemplate(props, updatedState, this.buyboxCallbacks),
            addToWishlist: getBuyboxProductAddToWishlist(props, updatedState, this.buyboxCallbacks),
            rating: !props.context.app.config.hideRating && getBuyboxProductRating(props),
            quantity: product.IsGiftCard ? undefined : getBuyboxProductQuantity(props, updatedState, this.buyboxCallbacks),
            inventoryLabel: getBuyBoxInventoryLabel(props),
            shopSimilarLook:
                this.props.config.enableShopSimilarLooks && !product.IsGiftCard
                    ? getBuyboxShopSimilarButton(props, ShopSimiliarButtonType.Looks)
                    : undefined,
            shopSimilarDescription:
                this.props.config.enableShopSimilarDescription && !product.IsGiftCard
                    ? getBuyboxShopSimilarButton(props, ShopSimiliarButtonType.Description)
                    : undefined,
            keyInPrice:
                this.props.config.enableKeyInPrice && updatedState.isCustomPriceSelected
                    ? getBuyboxKeyInPrice(props, updatedState, this.buyboxCallbacks)
                    : undefined,
            quantityLimitsMessages: getQuantityLimitsMessages(props, updatedState),
            max,
            defaultMaxQty: this.defaultMaxQty,
            catalogs: this.props.data.catalogs?.result?.filter(item => item.RecordId === catalogId),
            guaranteedItem: this._renderGuaranteedItem()
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    // 19790 - Guaranteed Item
    private _renderGuaranteedItem(): React.ReactNode {
        const {
            config: { guaranteedItemImage },
            data: { checkGuaranteedItem },
            resources: { guaranteedItemText }
        } = this.props;

        if (!checkGuaranteedItem.result?.isGuaranteedItem) {
            return null;
        }

        // if (guaranteedItemImage && guaranteedItemImage.imageSettings && guaranteedItemImage.src) {
        if (guaranteedItemImage && guaranteedItemImage.src) {
            // const imageSettings: Msdyn365.IImageSettings = {
            //     ...guaranteedItemImage.imageSettings
            // };
            return (
                <>
                    <div className='ms-buybox__media-gallery-guaranteed-item--image'>
                        {/* Allows transparent images */}
                        <img src={`${guaranteedItemImage.src || ''}`} />
                        {/* Does not allow transparent images */}
                        {/* <Msdyn365.Image
                            {...guaranteedItemImage}
                            gridSettings={request.gridSettings ?? {}}
                            imageSettings={imageSettings}
                            requestContext={request}
                            editProps={{
                                key: guaranteedItemImage ?? {},
                                requestContext: request,
                                moduleType: typeName,
                                imagePropertyName: 'guaranteedItemImage',
                                moduleId: id
                            }}
                        /> */}
                    </div>
                </>
            );
        }

        // If no image is added, display the default flag.
        return <div className='ms-buybox__media-gallery-guaranteed-item--flag'>{guaranteedItemText}</div>;
    }

    /**
     * Handles anchor click event and scrolls to the given bulk purchase module.
     * @param bulkPurchaseModule - Bulk purchase module.
     * @returns Handler.
     */
    private readonly _onBulkPurchaseLinkClick = (bulkPurchaseModule: Element) => () => {
        const boundingRect = bulkPurchaseModule.getBoundingClientRect();
        window.scrollTo(boundingRect.left, boundingRect.top);
    };

    private _renderBuyboxBulkPurchaseLink(): React.ReactNode {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Temporary switch for the feature which is under development.
        if (!this.props.context.app.config.isBulkPurchaseEnabled) {
            return null;
        }

        if (!msdyn365Commerce.isBrowser) {
            return null;
        }

        const bulkPurchaseModules = Array.from(document.getElementsByClassName(BulkPurchase.className));

        if (!ArrayExtensions.hasElements(bulkPurchaseModules)) {
            return null;
        }

        return (
            <Button
                className='ms-buybox__bulk-purchase-anchor'
                title={this.props.resources.bulkPurchaseLinkText}
                onClick={this._onBulkPurchaseLinkClick(bulkPurchaseModules[0])}
            >
                {this.props.resources.bulkPurchaseLinkText}
            </Button>
        );
    }

    private _updatePrice(newPrice: ProductPrice | undefined, customPrice: number | undefined = this.state.keyInPriceAmount): void {
        if (this.state.isCustomPriceSelected && newPrice) {
            newPrice.CustomerContextualPrice = customPrice;
        }
        this.setState({ productPrice: newPrice });
    }

    private _getProductVariant(dimensionsToUpdate: { [id: number]: string }) {
        const {
            data: {
                product: { result: product }
            },
            context: {
                actionContext,
                request: {
                    apiSettings: { channelId }
                }
            }
        } = this.props;

        const productDimensions = this.props.data.productDimensions.result!;

        // Step 3, Build the actually selected dimensions, prioritizing the information in state
        // over the information in data
        const mappedDimensions = productDimensions
            .map(dimension => {
                const dimensions = (dimension.dimensionValuesWithInventory ??
                    dimension.DimensionValues ??
                    []) as IDimensionValueForSelectedVariant[];
                return {
                    DimensionTypeValue: dimension.DimensionTypeValue,
                    DimensionValue:
                        dimensions.find(
                            value => value.DimensionValue?.RecordId.toString() === dimensionsToUpdate[dimension.DimensionTypeValue]
                        )?.DimensionValue ?? dimension.DimensionValue,
                    ExtensionProperties: dimension.ExtensionProperties
                };
            })
            .filter(dimension => dimension.DimensionValue);
        setDimensionValuesToQuery(this.props.context.actionContext.requestContext.url.requestUrl, mappedDimensions);

        // Step 4. Use these dimensions hydrate the product. Wrap this in a promise
        // so that places like add to cart can await it
        const selectedProductPromise = getSelectedVariant(
            new SelectedVariantInput(
                product!.MasterProductId ? product!.MasterProductId : product!.RecordId,
                channelId,
                undefined,
                undefined,
                this.props.context.request
            ),
            actionContext
        );
        this.setState({ selectedProduct: selectedProductPromise });

        return { productVariantPromise: selectedProductPromise, mappedDimensions };
    }

    private readonly _updateDimensions = async (): Promise<void> => {
        const product = this.props.data.product.result;

        const productDimensions = this.props.data.productDimensions.result;

        const hasProductDimensions = ArrayExtensions.hasElements(productDimensions);

        if (!product || !hasProductDimensions) {
            return;
        }

        const dimensionsToUpdate: { [id: number]: string } = { ...this.dimensions };
        this.setState({ isUpdatingDimension: true, isUpdatingDeliveryOptions: true });

        // Step 1: Clear error state to display relevant errors
        if (this.state.errorState.otherError || this.state.errorState.quantityError) {
            const clearErrorState = { ...this.state.errorState };
            clearErrorState.otherError = undefined;
            if (this.state.errorState.errorHost === 'ADDTOCART') {
                clearErrorState.quantityError = undefined;
                clearErrorState.errorHost = undefined;
            }
            this.setState({ errorState: clearErrorState });
        }

        // Step 2: Clear any errors indicating the dimension wasn't selected
        for (const key of Object.keys(dimensionsToUpdate)) {
            if (this.state.errorState.configureErrors[key]) {
                this.setState(previousState => {
                    const errorState = previousState.errorState;
                    errorState.configureErrors[key] = undefined;
                    return { errorState };
                });
            }
        }

        const data = this._getProductVariant(dimensionsToUpdate);
        const productVariant: SimpleProduct | null = await data.productVariantPromise;

        if (!productVariant) {
            return;
        }

        const promises: Promise<void>[] = [
            this._getDimensionsForSelectedVariant(productVariant, data.mappedDimensions),
            this._hydrateInventoryInfo(productVariant),
            this._hydratePrice(productVariant),
            this._hydrateDeliveryOptions(productVariant),
            this._updateQuantitiesInState(productVariant)
        ];

        await Promise.all(promises);
    };

    private async _getDimensionsForSelectedVariant(
        productVariant: SimpleProduct,
        mappedDimensions: {
            /* eslint-disable @typescript-eslint/naming-convention -- Retail proxy values */
            DimensionTypeValue: number;
            DimensionValue: ProductDimensionValue | undefined;
            ExtensionProperties: CommerceProperty[] | undefined;
            /* eslint-enable @typescript-eslint/naming-convention -- Retail proxy values */
        }[]
    ): Promise<void> {
        await getDimensionsForSelectedVariant(
            new GetDimensionsForSelectedVariantInput(
                productVariant.MasterProductId ? productVariant.MasterProductId : productVariant.RecordId,
                this.props.context.request.apiSettings.channelId,
                mappedDimensions,
                this.props.context.request
            ),
            this.props.context.actionContext
        );
    }

    private async _hydrateInventoryInfo(productVariant: SimpleProduct): Promise<void> {
        // Step 5. Use these dimensions hydrate the inventory. Wrap this in a promise
        // so that places like add to cart can await it
        const newAvailableQuantity = await getProductAvailabilitiesForSelectedVariant(
            new ProductAvailabilitiesForSelectedVariantInput(productVariant.RecordId, this.props.context.request.apiSettings.channelId),
            this.props.context.actionContext
        );

        const newShippingQuantity = newAvailableQuantity?.find(
            // @ts-expect-error
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (shipping: { deliveryType: any }) => shipping.deliveryType === DeliveryMode.shipping
        );
        const isCustomPriceSelected = productVariant.Dimensions?.find(
            dimension =>
                dimension.DimensionTypeValue === 4 && dimension.DimensionValue && dimension.DimensionValue.Value?.toLowerCase() === 'custom'
        );
        if (isCustomPriceSelected) {
            this.setState({ isCustomPriceSelected: true });
        } else {
            this.setState(previousState => {
                const errorState = previousState.errorState;
                errorState.customAmountError = undefined;
                return { isCustomPriceSelected: false, isPriceKeyedIn: false, errorState };
            });
        }

        if (newShippingQuantity) {
            this.setState({ productAvailableQuantity: newShippingQuantity });
        } else {
            this.setState({ productAvailableQuantity: undefined });
        }
    }

    private async _hydratePrice(productVariant: SimpleProduct): Promise<void> {
        // Step 6. Use these dimensions hydrate the product price.
        const newPrice = await getPriceForSelectedVariant(
            new PriceForSelectedVariantInput(productVariant.RecordId, this.props.context.request.apiSettings.channelId),
            this.props.context.actionContext
        );

        if (newPrice) {
            this._updatePrice(newPrice);
        }
    }

    private async _hydrateDeliveryOptions(productVariant: SimpleProduct): Promise<void> {
        const retailMultiplePickupFeatureState = this.props.data.featureState.result?.find(
            featureState => featureState.Name === 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature'
        );

        // Step 7. Use these dimensions hydrate the product delivery options.
        const newDeliveryOptions = await getDeliveryOptionsForSelectedVariant(
            new GetDeliveryOptionsForSelectedVariantInput(
                productVariant.RecordId,
                this.props.context.request.apiSettings.channelId,
                undefined,
                undefined,
                retailMultiplePickupFeatureState?.IsEnabled
            ),
            this.props.context.actionContext
        );

        if (newDeliveryOptions) {
            this.setState({ productDeliveryOptions: newDeliveryOptions });
        }
    }

    private async _updateQuantitiesInState(product: SimpleProduct): Promise<void> {
        const minValue = product.Behavior?.MinimumQuantity || 1;
        const multipleOf = product.Behavior?.MultipleOfQuantity || 1;
        const max: number = this._getMaxQuantityForCartLineItem(product);
        this.setState({
            min: minValue < multipleOf ? multipleOf : minValue,
            max: max
        });
    }

    private _getMaxQuantityForCartLineItem(product: SimpleProduct): number {
        if (product?.Behavior?.MaximumQuantity && product.Behavior.MaximumQuantity > 0) {
            return product.Behavior.MaximumQuantity;
        }

        const cartLineMaxQtyInSiteBuilder: number = this.props.context.app.config.maxQuantityForCartLineItem || this.defaultMaxQty;

        return cartLineMaxQtyInSiteBuilder;
    }

    private readonly _getDropdownName = (dimensionType: number, resources: IMilBuyboxResources): string => {
        const isGiftCard = this.props.data.product.result?.IsGiftCard;

        switch (dimensionType) {
            case 1: // ProductDimensionType.Color
                return resources.productDimensionTypeColor;
            case 2: // ProductDimensionType.Configuration
                return resources.productDimensionTypeConfiguration;
            case 3: // ProductDimensionType.Size
                return resources.productDimensionTypeSize;
            case 4: // ProductDimensionType.Style
                return isGiftCard ? resources.productDimensionTypeAmount : resources.productDimensionTypeStyle;
            default:
                return '';
        }
    };
}

export default Buybox;
